import React, { useState } from 'react';
import { X, Calendar, User, Phone, Mail } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { sendAppointmentEmail } from '../utils/emailService';

interface BookingPopupProps {
  isOpen: boolean;
  onClose: () => void;
}

const BookingPopup: React.FC<BookingPopupProps> = ({ isOpen, onClose }) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await sendAppointmentEmail({
        fullName: formData.name,
        phone: formData.phone,
        email: formData.email,
        department: formData.service,
        preferredDate: formData.date,
        message: formData.message
      });

      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        onClose();
        setFormData({
          name: '',
          email: '',
          phone: '',
          date: '',
          service: '',
          message: ''
        });
      }, 3000);
    } catch (error: any) {
      console.error('Error booking appointment:', error);
      setIsSubmitting(false);

      if (error.message === 'EMAIL_NOT_CONFIGURED') {
        alert('Email service is not yet configured. Please call us directly at +255 757 685 894 to book your appointment. We apologize for the inconvenience.');
      } else {
        alert('Failed to send appointment request. Please try again or call us at +255 757 685 894.');
      }
      return;
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 md:p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[95vh] md:max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-gray-200">
          <h2 className="text-lg md:text-xl font-bold text-gray-900">{t('appointment.bookAppointment')}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5 md:h-6 md:w-6" />
          </button>
        </div>

        {/* Success Message */}
        {showSuccess && (
          <div className="p-4 md:p-6 bg-green-50 border-b border-green-200">
            <div className="text-green-800 text-center">
              <div className="text-xl md:text-2xl mb-2">✅</div>
              <p className="font-medium text-sm md:text-base">{t('appointment.bookingSuccess')}</p>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 md:p-6 space-y-3 md:space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <User className="inline h-3 w-3 md:h-4 md:w-4 mr-1" />
              Full Name *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
              placeholder="Enter your full name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Mail className="inline h-3 w-3 md:h-4 md:w-4 mr-1" />
              Email Address
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
              placeholder="your.email@example.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Phone className="inline h-3 w-3 md:h-4 md:w-4 mr-1" />
              Phone Number *
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
              placeholder="+255 7XX XXX XXX"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="inline h-3 w-3 md:h-4 md:w-4 mr-1" />
                Preferred Date *
              </label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleInputChange}
                required
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
              />
            </div>

          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Clinic *
            </label>
            <select
              name="service"
              value={formData.service}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
            >
              <option value="">Select Clinic</option>
              <option value="General OPD">General OPD</option>
              <option value="Paediatrics">Paediatrics</option>
              <option value="Physician">Physician</option>
              <option value="Cardiology">Cardiology</option>
              <option value="O/Gynaecology">O/Gynaecology</option>
              <option value="General Surgery">General Surgery</option>
              <option value="Orthopaedics">Orthopaedics</option>
              <option value="ENT">ENT</option>
              <option value="Dental">Dental</option>
              <option value="Optician">Optician</option>
              <option value="Psychiatric">Psychiatric</option>
              <option value="Neurology">Neurology</option>
              <option value="Neurosurgery">Neurosurgery</option>
              <option value="Ophthalmology">Ophthalmology (Eye Care)</option>
              <option value="ENT">ENT (Ear, Nose & Throat)</option>
              <option value="CTC">CTC (Care and Treatment Center)</option>
              <option value="Mortuary">Mortuary Services</option>
              <option value="Physiotherapy">Physiotherapy</option>
              <option value="Clinical Psychology">Clinical Psychology</option>
              <option value="Urology">Urology</option>
              <option value="Gastro Enterology">Gastro Enterology</option>
              <option value="Diabetic">Diabetic</option>
              <option value="Dermatology">Dermatology</option>
              <option value="Anaesthesiologist and Critical Care">Anaesthesiologist and Critical Care</option>
              <option value="Ophthalmology">Ophthalmology</option>
              <option value="Haematology">Haematology</option>
              <option value="Paediatric Surgery">Paediatric Surgery</option>
              <option value="Pathology">Pathology</option>
              <option value="Respiratory and Physician">Respiratory and Physician</option>
              <option value="Physician and Nephrology">Physician and Nephrology</option>
              <option value="Oncology">Oncology</option>
              <option value="Optometrist">Optometrist</option>
              <option value="Occupational Therapy">Occupational Therapy</option>
              <option value="Emergency Specialist">Emergency Specialist</option>
              <option value="Radiologist">Radiologist</option>
              <option value="TB and CTC Clinic">TB and CTC Clinic</option>
              <option value="Cardiothoracic and Vascular Surgery">Cardiothoracic and Vascular Surgery</option>
              <option value="General and Paediatric Surgeon">General and Paediatric Surgeon</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Additional Message
            </label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
              placeholder="Please describe your symptoms or reason for visit..."
            />
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-blue-600 text-white py-2 md:py-3 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm md:text-base"
          >
            {isSubmitting ? 'Booking...' : 'Book Appointment'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default BookingPopup;